import { Patient, Prescription } from '@/types';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';

export const printPrescription = (patient: Patient, prescriptions: Prescription[], doctors: any[] = []) => {
  const { profile } = useHospitalProfileStore.getState();
  
  // Get the latest issued prescription
  const issuedPrescriptions = prescriptions.filter(p => p.status === 'issued');
  const latestPrescription = issuedPrescriptions.sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
  
  if (!latestPrescription) {
    console.error('No issued prescription found');
    return;
  }
  
  // Create a new window for printing
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    console.error('Could not open print window');
    return;
  }

  const currentDate = new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Format patient ID
  const formatPatientId = (id: string) => {
    return id.replace(/(.{4})(.{4})(.{4})(.{4})/, '$1-$2-$3-$4');
  };

  // Get doctor name
  const getDoctorName = (doctorId: string) => {
    if (!doctorId) return 'Unassigned';
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? doctor.name : 'Unassigned';
  };

  // Format prescription date
  const prescriptionDate = new Date(latestPrescription.createdAt).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Prescription - ${patient.name}</title>
      <style>
        @media print {
          @page {
            size: A4;
            margin: 0.5in;
          }
        }
        
        body {
          font-family: 'Arial', sans-serif;
          font-size: 12px;
          line-height: 1.4;
          margin: 0;
          padding: 0;
          color: #333;
        }
        
        .header {
          text-align: center;
          border-bottom: 2px solid #2563eb;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        
        .hospital-name {
          font-size: 24px;
          font-weight: bold;
          color: #2563eb;
          margin-bottom: 5px;
        }
        
        .hospital-details {
          font-size: 11px;
          color: #666;
          margin-bottom: 5px;
        }
        
        .patient-section {
          margin-bottom: 25px;
        }
        
        .section-title {
          font-size: 14px;
          font-weight: bold;
          color: #2563eb;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 5px;
          margin-bottom: 10px;
        }
        
        .patient-info {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .info-item {
          margin-bottom: 8px;
        }
        
        .info-label {
          font-weight: bold;
          color: #374151;
        }
        
        .info-value {
          color: #6b7280;
        }
        
        .prescription-section {
          margin-top: 30px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
        }
        
        .prescription-title {
          font-size: 18px;
          font-weight: bold;
          color: #2563eb;
          text-align: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 10px;
        }
        
        .prescription-id {
          text-align: right;
          font-size: 10px;
          color: #666;
          margin-bottom: 15px;
        }
        
        .medication-item {
          margin-bottom: 15px;
          padding: 10px;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
        
        .medication-name {
          font-weight: bold;
          font-size: 14px;
          color: #2563eb;
          margin-bottom: 5px;
        }
        
        .medication-details {
          font-size: 12px;
          color: #374151;
          margin-bottom: 3px;
        }
        
        .instructions {
          margin-top: 20px;
          padding: 15px;
          background-color: #f8fafc;
          border-radius: 4px;
        }
        
        .instructions-title {
          font-weight: bold;
          color: #2563eb;
          margin-bottom: 10px;
        }
        
        .doctor-signature {
          display: flex;
          justify-content: space-between;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }
        
        .signature-box {
          width: 200px;
          text-align: center;
        }
        
        .signature-line {
          border-bottom: 1px solid #333;
          height: 20px;
          margin-bottom: 5px;
        }
        
        .footer {
          margin-top: 20px;
          text-align: center;
          font-size: 10px;
          color: #6b7280;
          border-top: 1px solid #e5e7eb;
          padding-top: 10px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="hospital-name">${profile.name}</div>
        <div class="hospital-details">
          ${profile.address}<br>
          Phone: ${profile.phone1} | Email: ${profile.email}<br>
          ${profile.website}
        </div>
      </div>
      
      <div class="patient-section">
        <div class="section-title">Patient Information</div>
        <div class="patient-info">
          <div>
            <div class="info-item">
              <span class="info-label">Name:</span>
              <span class="info-value">${patient.name}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Age:</span>
              <span class="info-value">${patient.age}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Gender:</span>
              <span class="info-value">${patient.gender}</span>
            </div>
          </div>
          <div>
            <div class="info-item">
              <span class="info-label">Patient ID:</span>
              <span class="info-value">${formatPatientId(patient.id)}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Date:</span>
              <span class="info-value">${prescriptionDate}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Doctor:</span>
              <span class="info-value">${getDoctorName(latestPrescription.doctorId)}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="prescription-section">
        <div class="prescription-title">PRESCRIPTION</div>
        <div class="prescription-id">Prescription ID: ${latestPrescription.id}</div>
        
        ${latestPrescription.medications && latestPrescription.medications.length > 0 ? 
          latestPrescription.medications.map((med: any) => `
            <div class="medication-item">
              <div class="medication-name">${med.name || med.medication || 'Medication'}</div>
              <div class="medication-details"><strong>Dosage:</strong> ${med.dosage || 'As directed'} ${med.doseType ? med.doseType : ''}</div>
              <div class="medication-details"><strong>Frequency:</strong> ${med.frequency || 'As needed'}</div>
              <div class="medication-details"><strong>Duration:</strong> ${med.duration || 'As prescribed'}</div>
              ${med.instructions ? `<div class="medication-details"><strong>Instructions:</strong> ${med.instructions}</div>` : ''}
            </div>
          `).join('') : 
          '<div class="medication-item">No medications prescribed</div>'
        }
        
        ${latestPrescription.instructions ? `
          <div class="instructions">
            <div class="instructions-title">General Instructions:</div>
            <div>${latestPrescription.instructions}</div>
          </div>
        ` : ''}
        
        ${latestPrescription.notes ? `
          <div class="instructions">
            <div class="instructions-title">Additional Notes:</div>
            <div>${latestPrescription.notes}</div>
          </div>
        ` : ''}
        
        <div class="doctor-signature">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div>Doctor's Signature</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div>Date</div>
          </div>
        </div>
      </div>
      
      <div class="footer">
        Generated on ${currentDate} | ${profile.name}
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();
  
  // Wait for content to load, then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
};
